/**
 * Retention Page
 *
 * User retention, churn, and health metrics.
 * Shows retention rates, member engagement, and org health distribution.
 */

import { useState, useEffect } from 'react';
import { founderDashboardAPI } from '../../services/api';
import { useEnvironment } from '../../context/EnvironmentContext';
import KPICard from '../../components/founder-dashboard/KPICard';
import DistributionChart from '../../components/founder-dashboard/DistributionChart';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

export default function RetentionPage() {
  const { currentEnv } = useEnvironment();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null); // Track which env the data/error belongs to

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getRetention();
      setData(response.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[RetentionPage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear all state and refetch when environment changes
    setData(null);
    setError(null);
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentEnv]);

  // Only show error if it's from the current environment
  if (error && dataEnv === currentEnv) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-medium">Error loading retention data</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const retention = data?.retention || {};
  const churn = data?.churn || {};
  const memberEngagement = data?.memberEngagement || {};
  const organizationHealth = data?.organizationHealth || {};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Retention Metrics</h2>
          <p className="text-gray-500 mt-1">
            User retention, churn, and engagement health
          </p>
        </div>
        <RefreshIndicator lastUpdated={data?.timestamp} onRefresh={fetchData} loading={loading} />
      </div>

      {/* Retention Rates */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <ArrowTrendingUpIcon className="w-6 h-6 text-green-500" />
          Retention Rates
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <KPICard
            title="Weekly Retention"
            value={retention.weekly}
            format="percent"
            loading={loading}
            subtitle="Users returning week-over-week"
          />
          <KPICard
            title="Monthly Retention"
            value={retention.monthly}
            format="percent"
            loading={loading}
            subtitle="Users returning month-over-month"
          />
        </div>
      </section>

      {/* Churn Metrics */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <ArrowTrendingDownIcon className="w-6 h-6 text-red-500" />
          Churn
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KPICard
            title="Churn Rate"
            value={churn.rate}
            format="percent"
            loading={loading}
            subtitle="Last 30 days"
          />
          <KPICard
            title="Organizations Churned"
            value={churn.churned}
            loading={loading}
            subtitle="Last 30 days"
          />
          <KPICard
            title="Active Subscriptions"
            value={churn.active}
            loading={loading}
          />
        </div>
      </section>

      {/* Member Engagement Distribution */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          Member Engagement Distribution
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Distribution Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-4">Engagement Tiers</h4>
            {loading ? (
              <div className="h-48 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <DistributionChart
                data={{
                  'Highly Engaged': memberEngagement.highlyEngaged || 0,
                  'Engaged': memberEngagement.engaged || 0,
                  'At Risk': memberEngagement.atRisk || 0,
                }}
                height={200}
              />
            )}
          </div>

          {/* Stats Cards */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-4">Member Breakdown</h4>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-12 animate-pulse bg-gray-100 rounded"></div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    <span className="text-gray-700">Highly Engaged</span>
                  </div>
                  <span className="text-xl font-bold text-green-600">
                    {memberEngagement.highlyEngaged || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                    <span className="text-gray-700">Engaged</span>
                  </div>
                  <span className="text-xl font-bold text-blue-600">
                    {memberEngagement.engaged || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
                    <span className="text-gray-700">At Risk</span>
                  </div>
                  <span className="text-xl font-bold text-yellow-600">
                    {memberEngagement.atRisk || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-500 font-medium">Total Members</span>
                  <span className="text-xl font-bold text-gray-900">
                    {memberEngagement.total || 0}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Organization Health Distribution */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          Organization Health Distribution
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Distribution Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-4">Health Status</h4>
            {loading ? (
              <div className="h-48 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <DistributionChart
                data={{
                  'Healthy': organizationHealth.healthy || 0,
                  'Needs Attention': organizationHealth.needsAttention || 0,
                  'At Risk': organizationHealth.atRisk || 0,
                }}
                height={200}
              />
            )}
          </div>

          {/* Stats Cards */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-4">Organization Breakdown</h4>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-12 animate-pulse bg-gray-100 rounded"></div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    <span className="text-gray-700">Healthy</span>
                  </div>
                  <span className="text-xl font-bold text-green-600">
                    {organizationHealth.healthy || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
                    <span className="text-gray-700">Needs Attention</span>
                  </div>
                  <span className="text-xl font-bold text-yellow-600">
                    {organizationHealth.needsAttention || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <XCircleIcon className="w-5 h-5 text-red-500" />
                    <span className="text-gray-700">At Risk</span>
                  </div>
                  <span className="text-xl font-bold text-red-600">
                    {organizationHealth.atRisk || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-500 font-medium">Total Organizations</span>
                  <span className="text-xl font-bold text-gray-900">
                    {organizationHealth.total || 0}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
